import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  MenuItem
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const PartsProcurementWO = ({ open, onClose, workOrder, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    if (workOrder && open) {
      // Pre-fill title based on original work order
      setFormData({
        title: `Parts Procurement for ${workOrder.title}`,
        description: `Parts procurement work order for: ${workOrder.title}`,
        priority: workOrder.priority || 'MEDIUM'
      });
    }
  }, [workOrder, open]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (!formData.title.trim()) {
        setError('Title is required');
        return;
      }

      // Check if Planning has provided procurement requirements
      if (!workOrder.partsProcurementRequirements || 
          !workOrder.partsProcurementRequirements.partNumbers || 
          workOrder.partsProcurementRequirements.partNumbers.length === 0) {
        setError('Planning must provide parts procurement requirements before creating parts procurement work order');
        return;
      }

      await axios.post(`/api/maintenance/${workOrder._id}/create-parts-procurement-wo`, {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        priority: formData.priority
      });

      showToast('Parts procurement work order created successfully', 'success');
      onSuccess();
      handleClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create parts procurement work order';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'MEDIUM'
    });
    setError('');
    onClose();
  };

  const procurementRequirements = workOrder?.partsProcurementRequirements;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Parts Procurement Work Order</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Original Work Order: <strong>{workOrder?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aircraft: <strong>{workOrder?.aircraftId?.registration || 'Unknown'}</strong>
          </Typography>
        </Box>

        {procurementRequirements && procurementRequirements.partNumbers && procurementRequirements.partNumbers.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Procurement Requirements (from Planning):</strong>
            </Typography>
            <Typography variant="body2">
              Part Numbers: {procurementRequirements.partNumbers.join(', ')}
            </Typography>
            {procurementRequirements.quantities && procurementRequirements.quantities.length > 0 && (
              <Typography variant="body2">
                Quantities: {procurementRequirements.quantities.join(', ')}
              </Typography>
            )}
            {procurementRequirements.descriptions && procurementRequirements.descriptions.some((d) => String(d || '').trim()) && (
              <Typography variant="body2">
                Descriptions: {procurementRequirements.descriptions.filter((d) => String(d || '').trim()).join(' | ')}
              </Typography>
            )}
            {procurementRequirements.specifications && (
              <Typography variant="body2">
                Specifications: {procurementRequirements.specifications}
              </Typography>
            )}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {(!procurementRequirements || !procurementRequirements.partNumbers || procurementRequirements.partNumbers.length === 0) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Planning must provide parts procurement requirements before creating parts procurement work order.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            >
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="CRITICAL">Critical</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.title.trim() || !procurementRequirements?.partNumbers?.length}
        >
          {loading ? 'Creating...' : 'Create Parts Procurement WO'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartsProcurementWO;

